import { s3ImageLink } from "@/utils/links";
import { Checkbox, Tab } from "aha-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  OtherPaymentMethod,
  PaymentProvider,
  SavedPaymentMethod,
} from "./types";
import { formatCardBrand } from "./cardBrand";

const CARD_BRAND_IMAGES: Record<string, string> = {
  visa: "https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg",
  mastercard: "https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg",
  amex: "https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg",
  discover: "https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg",
  diners: "https://js.stripe.com/v3/fingerprinted/img/diners-fbcbd3360f8e3f629cdaa80e93abdb8b.svg",
  jcb: "https://js.stripe.com/v3/fingerprinted/img/jcb-271fd06e6e7a2c52692ffa91a95fb64f.svg",
  unionpay: "https://js.stripe.com/v3/fingerprinted/img/unionpay-8a10aefc7295216c338ba4e1224627a1.svg",
};

const paymentMethodIcons: Record<string, string> = {
  ach_direct_debit: "https://api.iconify.design/mdi/bank-transfer-in.svg",
  airwallex_pay:
    "https://upload.wikimedia.org/wikipedia/commons/4/47/Airwallex_logo_%282025%29.png",
  applepay: "https://api.iconify.design/logos/apple-pay.svg",
  googlepay: "https://api.iconify.design/logos/google-pay.svg",
  klarna: "https://cdn.jsdelivr.net/npm/simple-icons@16.27.1/icons/klarna.svg",
  paysafecard: "https://www.svgrepo.com/show/83879/paysafecard-logo.svg",
  paysafecash: "https://api.iconify.design/simple-icons/paysafe.svg",
  skrill:
    "https://commons.wikimedia.org/wiki/Special:Redirect/file/Skrill_logo_2025.svg",
  us_bank_account: "https://api.iconify.design/mdi/bank-outline.svg",
  link:
    "https://images.seeklogo.com/logo-png/61/1/link-by-stripe-logo-png_seeklogo-616414.png",
  wechatpay:
    "https://cdn.jsdelivr.net/gh/datatrans/payment-logos@master/assets/apm/wechat-pay.svg",
};

const paymentMethodNames: Record<string, string> = {
  ach_direct_debit: "ACH Direct Debit",
  airwallex_pay: "Airwallex Pay",
  applepay: "Apple Pay",
  googlepay: "Google Pay",
  klarna: "Klarna",
  us_bank_account: "US Bank Account",
  link: "Link",
  wechatpay: "WeChat Pay",
};

function CardBrandIcon({ brand }: { brand: string }) {
  const { t } = useTranslation();

  return (
    <span className="flex h-7 w-10 items-center justify-center rounded border bg-white p-1">
      <img
        className="max-h-full max-w-full"
        src={CARD_BRAND_IMAGES[brand.toLowerCase()] || `${s3ImageLink}credit-card.svg`}
        alt={t("components.chargeModal.paymentMethodSection.cardIcon", {
          brand,
          defaultValue: "{brand} card icon",
        })}
        onError={(event) => {
          event.currentTarget.src = `${s3ImageLink}credit-card.svg`;
        }}
      />
    </span>
  );
}

interface Props {
  provider: PaymentProvider;
  methods: SavedPaymentMethod[];
  selectedId: string | null;
  error?: string;
  onProviderChange: (provider: PaymentProvider) => void;
  onSelect: (id: string) => void;
  onManage: () => void;
  onAdd: () => void;
  otherPaymentMethods?: OtherPaymentMethod[];
  onOtherPaymentMethodSelect?: (
    method: OtherPaymentMethod
  ) => Promise<void> | void;
}

const formatMethodType = (methodType: string) =>
  paymentMethodNames[methodType] ||
  methodType
    .split("_")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");

export default function PaymentMethodSection({
  provider,
  methods,
  selectedId,
  error,
  onProviderChange,
  onSelect,
  onManage,
  onAdd,
  otherPaymentMethods = [],
  onOtherPaymentMethodSelect,
}: Props) {
  const { t } = useTranslation();
  const [startingOtherPaymentMethod, setStartingOtherPaymentMethod] =
    useState<string>();

  const handleOtherPaymentMethodSelect = async (method: OtherPaymentMethod) => {
    if (!onOtherPaymentMethodSelect || startingOtherPaymentMethod) return;

    setStartingOtherPaymentMethod(method.methodType);
    try {
      await onOtherPaymentMethodSelect(method);
    } finally {
      setStartingOtherPaymentMethod(undefined);
    }
  };

  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[#171717] md:gap-2 md:text-base">
          <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#1E1E1E] text-[8px] text-white md:h-[18px] md:w-[18px] md:text-[11px]">2</span>
          {t("components.chargeModal.paymentMethodSection.title", {
            defaultValue: "Payment method",
          })}
        </h3>
        {error && <span className="text-xs text-[#D92D20]">{error}</span>}
      </div>

      <div className="mb-4">
        <Tab
          value={provider}
          variant="underline"
          mode="value"
          className={
            provider === "airwallex"
              ? "[&>div:first-child]:!left-0 [&>div:first-child]:!w-[76px] md:[&>div:first-child]:!w-[94px]"
              : "[&>div:first-child]:!left-[108px] [&>div:first-child]:!w-[30px] md:[&>div:first-child]:!left-[126px] md:[&>div:first-child]:!w-[38px]"
          }
          onChange={(value: string | number) =>
            onProviderChange(value as PaymentProvider)
          }
          items={[
            {
              key: "airwallex",
              label: (
                <img
                  className="h-[16px] w-[76px] object-contain md:h-[22px] md:w-[94px]"
                  src={`${s3ImageLink}${provider === "airwallex" ? "airwallex-active.png" : "airwallex-default.png"}`}
                  alt="Airwallex"
                />
              ),
            },
            {
              key: "stripe",
              label: (
                <img
                  className="h-[16px] w-[30px] object-contain md:h-[22px] md:w-[38px]"
                  src={`${s3ImageLink}${provider === "stripe" ? "stripe-active.png" : "stirpe-default.png"}`}
                  alt="Stripe"
                />
              ),
            },
          ]}
        />
      </div>

      <div className="mb-3 flex items-center justify-between md:mb-4">
        <span className="text-xs font-semibold md:text-sm">
          {t("components.chargeModal.paymentMethodSection.savedPaymentMethod", {
            defaultValue: "Saved payment method",
          })}
        </span>
        {methods.length > 0 && <button type="button" onClick={onManage} className="text-xs text-[#4E65FF]">{t("components.chargeModal.paymentMethodSection.manage", { defaultValue: "Manage" })}</button>}
      </div>

      {methods.length === 0 ? (
        <div
          key={`${provider}-empty`}
          className="flex items-center gap-3 rounded-xl border border-dashed border-[#D5D5D5] p-4 md:gap-4 md:p-5"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#7F7BFF] to-[#F09BD5] text-white md:h-11 md:w-11 md:rounded-xl">▭</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold md:text-base">
              {t("components.chargeModal.paymentMethodSection.addPaymentMethodTitle", {
                defaultValue: "Add a payment method to continue",
              })}
            </div>
            <div className="mt-1 text-xs text-[#777] md:text-sm">
              {t("components.chargeModal.paymentMethodSection.addPaymentMethodDescription", {
                defaultValue: "Cards and local payment methods supported.",
              })}
            </div>
          </div>
          <button type="button" onClick={onAdd} className="h-8 rounded-md bg-[#FF5A1F] px-3 text-xs font-semibold text-white md:h-11 md:rounded-lg md:px-5 md:text-sm">{t("components.chargeModal.paymentMethodSection.add", { defaultValue: "Add" })}</button>
        </div>
      ) : (
        <div key={`${provider}-saved`} className="space-y-2">
          {methods.map((method, index) => {
            const expiresLabel = method.expires
              ? t("components.chargeModal.paymentMethodSection.expires", {
                  date: method.expires,
                  defaultValue: "Expires {date}",
                })
              : "";
            const defaultLabel = method.isDefault
              ? t("components.chargeModal.paymentMethodSection.default", {
                  defaultValue: "Default",
                })
              : "";
            const statusLabel = `${expiresLabel}${expiresLabel && defaultLabel ? " · " : ""}${defaultLabel}`;

            return (
              <div
                key={`${method.provider}-${method.id}-${index}`}
                role="radio"
                aria-checked={selectedId === method.id}
                tabIndex={0}
                onClick={() => onSelect(method.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(method.id);
                  }
                }}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${selectedId === method.id ? "border-[#FF5A1F] bg-[#FFF8F4]" : "border-[#E1E1E1]"}`}
              >
                <Checkbox checked={selectedId === method.id} />
                <CardBrandIcon brand={method.brand} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">
                    {t("components.chargeModal.paymentMethodSection.cardEnding", {
                      brand: formatCardBrand(method.brand),
                      last4: method.last4,
                      defaultValue: "{brand} ending {last4}",
                    })}
                  </span>
                  {statusLabel && (
                    <span key={statusLabel} className="block text-xs text-[#888]">
                      {statusLabel}
                    </span>
                  )}
                </span>
                {method.feeLabel && (
                  <span className="rounded-full border border-[#FFD7C2] bg-[#FFF5EF] px-2 py-1 text-xs text-[#B54708]">
                    {method.feeLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {otherPaymentMethods.length > 0 && (
        <div className="mt-4">
          <span className="text-sm font-semibold">
            {t("components.chargeModal.paymentMethodSection.otherPaymentMethods", {
              defaultValue: "Other payment methods",
            })}
          </span>
          <div className="mt-3 grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-2">
            {otherPaymentMethods.map((method) => {
              const isStarting = startingOtherPaymentMethod === method.methodType;
              const icon = paymentMethodIcons[method.methodType];
              const methodName = formatMethodType(method.methodType);

              return (
                <button
                  key={method.methodType}
                  type="button"
                  disabled={Boolean(startingOtherPaymentMethod)}
                  onClick={() => handleOtherPaymentMethodSelect(method)}
                  className="flex h-[38px] min-w-0 items-center gap-2 rounded-md border border-[#e1e1e1] px-3 text-left text-sm font-medium text-[#171717] disabled:cursor-not-allowed disabled:opacity-60 md:h-9 md:rounded-lg"
                >
                  {icon && (
                    <span className="flex h-5 w-10 shrink-0 items-center justify-center">
                      <img
                        className="max-h-full max-w-full object-contain"
                        src={icon}
                        alt={`${methodName} icon`}
                      />
                    </span>
                  )}
                  <span className="truncate">
                    {isStarting
                      ? t("components.chargeModal.paymentMethodSection.redirecting", {
                          defaultValue: "Redirecting…",
                        })
                      : methodName}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
